/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import mixpanel from "mixpanel-browser";

//providers
import { useAuth } from "@/Providers/Auth";
import { useBrand } from "@/Providers/Brand";
import { useStaticData } from "@/Providers/Data";
import { useGeneralSettings } from "@/Providers/General";

//helpers
import { useBrandPaymentHandler } from "@/Helpers/payments/brandPayments";
import { useToast } from "@/shadcnComponents/ui/use-toast";
import {
  CollabStats,
  LiveCampaignData,
  OverallPerformanceData,
  SelectedInfluencer,
} from "../CampaignInterface/LiveCampaignInterface";
import { sampleLiveCampaignData } from "../Helpers/Helper";

export const useLiveCampaignHooks = () => {
  const { collabId } = useParams();
  const brandState = useBrand();
  const staticDataState = useStaticData();
  const generalState = useGeneralSettings();
  const { handlePaymentClick } = useBrandPaymentHandler();
  const { toast } = useToast();
  const authState = useAuth();

  const [lastPostTrackingTime, setLastPostTrackingTime] = useState<Date>(null);
  const [collabData, setCollabData] = useState<LiveCampaignData>(null);
  const [selectedInfluencers, SetSelectedInfluencers] =
    useState<SelectedInfluencer[]>(null);
  const [masterCollabGoals, setMasterCollabGoals] = useState<
    {
      CollabGoalsName: string;
      _id: string;
    }[]
  >([]);
  const [masterContentCategory, setMasterContentCategory] = useState<
    { categoryName: string; _id: string }[]
  >([]);
  const [masterAudienceCategory, setMasterAudienceCategory] = useState<
    { LkdAudienceTypeName: string; _id: string }[]
  >([]);
  const [collabStats, setCollabStats] = useState<CollabStats>({
    acceptedInfluencerLength: 0,
    draftSubmittedInfluencerLength: 0,
    approvedDraftLength: 0,
    suggestedChangesLength: 0,
    currentStatus: "Review",
    paidAlready: 0,
    isLiveDatePresent: false,
  });
  const [Amount, setAmount] = useState<number>();
  const [gst, setGst] = useState<number>();

  const [currentStep, setCurrentStep] = useState(1);
  const [openApprovePopup, setOpenApprovePopup] = useState(false);
  const [ApproveSection, setApproveSection] = useState("Approved");
  const [SuggestChanges, setSuggestChanges] = useState(null);
  const [currentInfluencerID] = useState<string>(null);
  const [overallPerformanceData, setOverallPerformanceData] =
    useState<OverallPerformanceData>(null);
  const [openSuccessPage, setOpenSuccessPage] = useState(false);
  const [loadingTracking, setLoadingTracking] = useState(false);
  const [draftApproved, setDraftApproved] = useState(false);
  const [changesSuggested, setChangesSuggested] = useState(false);
  const query = new URLSearchParams(window.location.search);
  const section = query?.get("section");
  const sampleId = query?.get("sampleId");

  //coupon states
  const [coupon, setCoupon] = useState<string>(null);
  const [discountedPrice, setDiscountedPrice] = useState<number>(null);

  //Add Funds States
  const [addFundsAmount, setAddFundsAmount] = useState(15000);
  const [addFundsAmountGst, setAddFundsAmountGst] = useState(0);

  //payment states
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<
    "card" | "upi" | null
  >(null);
  const [usedCollaborationCredits, setUsedCollaborationCredits] = useState(0);
  const [usedAnchorsCredits, setUsedAnchorsCredits] = useState(0);
  const [usedCampaignAddOn, setUsedCampaignAddOn] = useState(0);
  const [collaborationCharges, setCollabCharges] = useState(0);
  const [campaignCharges, setCampaignCharges] = useState(0);

  const getCollabData = async () => {
    generalState.setLoading(true);

    if (collabId === "sample") {
      setCollabData(sampleLiveCampaignData?.collab);
      SetSelectedInfluencers(sampleLiveCampaignData?.selectedInfluencers);
      setCollabStats({
        acceptedInfluencerLength:
          sampleLiveCampaignData?.acceptedInfluencerLength,
        draftSubmittedInfluencerLength:
          sampleLiveCampaignData?.draftSubmittedInfluencerLength,
        approvedDraftLength: sampleLiveCampaignData?.approvedDraftLength,
        suggestedChangesLength: sampleLiveCampaignData?.suggestedChangesLength,
        currentStatus: sampleLiveCampaignData?.currentStatus as
          | "Review"
          | "Live",
        paidAlready: sampleLiveCampaignData?.paidAlready,
        isLiveDatePresent: sampleLiveCampaignData?.isLiveDatePresent,
      });
      setDiscountedPrice(sampleLiveCampaignData?.discountedPrice || 0);
      setUsedCollaborationCredits(
        sampleLiveCampaignData?.usedCollaborationCredits || 0
      );
      setUsedAnchorsCredits(sampleLiveCampaignData?.usedAnchorsCredits || 0);
      setUsedCampaignAddOn(sampleLiveCampaignData?.usedCampaignAddOn || 0);
      generalState?.setLoading(false);

      return;
    }

    const result = await brandState?.getCollabData(collabId, sampleId);
    if (result) {
      setCollabData(result?.collab);
      SetSelectedInfluencers(result?.selectedInfluencers);
      setCollabStats({
        acceptedInfluencerLength: result?.acceptedInfluencerLength,
        draftSubmittedInfluencerLength: result?.draftSubmittedInfluencerLength,
        approvedDraftLength: result?.approvedDraftLength,
        suggestedChangesLength: result?.suggestedChangesLength,
        currentStatus: result?.currentStatus,
        paidAlready: result?.paidAlready,
        isLiveDatePresent: result?.isLiveDatePresent,
      });
      setCoupon(result?.couponCode);
      setDiscountedPrice(result?.discountedPrice || 0);
      setUsedCollaborationCredits(result?.usedCollaborationCredits || 0);
      setUsedAnchorsCredits(result?.usedAnchorsCredits || 0);
      setUsedCampaignAddOn(result?.usedCampaignAddOn || 0);
      generalState?.setLoading(false);
    } else {
      generalState.setLoading(false);
    }
  };

  useEffect(() => {
    if (collabData?.Budget?.perCollaborationCharges) {
      setCollabCharges(collabData?.Budget?.perCollaborationCharges);
    }
    if (collabData?.Budget?.perCampaignCharges) {
      setCampaignCharges(collabData?.Budget?.perCampaignCharges);
    }
  }, [collabData?.Budget]);

  useEffect(() => {
    mixpanel.track("Brand Live/Review Campaign Details Page visited");

    staticDataState?.getNewDataFromType("Goal / KPI").then((e) => {
      setMasterCollabGoals(e);
    });
    staticDataState?.getNewDataFromType("Content Type").then((e) => {
      setMasterContentCategory(e);
    });
    staticDataState
      ?.getNewDataFromType("lkd_super_master_audienceType")
      .then((e) => {
        setMasterAudienceCategory(e);
      });
    getCollabData();
  }, []);
  useEffect(() => {
    if (section === "Review") {
      setCurrentStep(2);
    } else if (section === "Payment") {
      setCurrentStep(3);
    } else if (section === "Date") {
      setCurrentStep(4);
    } else if (section === "Live") {
      setCurrentStep(5);
    } else if (section === "Report") {
      setCurrentStep(6);
    }
  }, [section]);
  useEffect(() => {
    const overallPerformance = async () => {
      const billAmount =
        collabData?.campaignInfo?.collabType === "fixed"
          ? selectedInfluencers?.reduce(
              (acc, item) => acc + item?.discountedApproxEarning,
              0
            )
          : collabData?.ROI?.safeExpense?.max +
            collabData?.Budget?.addedFundsAmount +
            (collabData?.Budget?.anchorsCharges || 0);

      const totalImpressions = selectedInfluencers
        ?.filter((item) => item?.brandApproval === "accepted")
        ?.reduce(
          (acc, item) => acc + parseInt(item?.postTracking?.impressions || "0"),
          0
        );
      const totalLikes = selectedInfluencers
        ?.filter((item) => item?.brandApproval === "accepted")
        ?.reduce(
          (acc, item) => acc + parseInt(item?.postTracking?.likes || "0"),
          0
        );
      const totalComments = selectedInfluencers
        ?.filter((item) => item?.brandApproval === "accepted")
        ?.reduce(
          (acc, item) => acc + parseInt(item?.postTracking?.comments || "0"),
          0
        );
      const totalReposts = selectedInfluencers
        ?.filter((item) => item?.brandApproval === "accepted")
        ?.reduce(
          (acc, item) => acc + parseInt(item?.postTracking?.reposts || "0"),
          0
        );
      const totalShares = selectedInfluencers
        ?.filter((item) => item?.brandApproval === "accepted")
        ?.reduce(
          (acc, item) => acc + parseInt(item?.postTracking?.shares || "0"),
          0
        );

      const acceptedInfluencers = selectedInfluencers?.filter(
        (item) =>
          item?.brandApproval === "accepted" &&
          item?.postTracking?.engagementPercentage > 0
      );

      const totalEngagementPercentage = acceptedInfluencers?.reduce(
        (acc, item) => {
          return acc + (item?.postTracking?.engagementPercentage || 0);
        },
        0
      );

      const avgEngagementPercentage =
        totalImpressions > 0
          ? (((totalLikes || 1) + (totalComments || 1) + (totalReposts || 1)) /
              totalImpressions) *
            100
          : 0;
      const acceptedCampaignInfluencers = selectedInfluencers?.filter(
        (item) => item?.response === "accepted"
      );
      const maxSpendAmount = selectedInfluencers
        ?.filter((item) => item?.brandApproval === "accepted")
        ?.reduce((acc, item) => acc + item?.maxPrice, 0);

      const amountSpendOnInfluencers = selectedInfluencers
        ?.filter((item) => item?.brandApproval === "accepted")
        ?.reduce(
          (acc, item) =>
            acc +
            item?.campaignCPI *
              parseInt(item?.postTracking?.impressions || "0"),
          0
        );

      const spentAmount =
        (maxSpendAmount
          ? Math.min(amountSpendOnInfluencers, maxSpendAmount)
          : amountSpendOnInfluencers) +
        (selectedInfluencers?.filter((i) => i.trackingLink !== undefined)
          ?.length > 0
          ? (collabData?.Budget?.anchorsCharges /
              (acceptedCampaignInfluencers?.length || 1)) *
              (selectedInfluencers?.filter((i) => i.trackingLink !== undefined)
                ?.length || 1) || 0
          : 0);

      const balanceRemaining = billAmount - spentAmount;

      setOverallPerformanceData({
        ...overallPerformanceData,
        totalImpressions,
        totalLikes,
        totalComments,
        totalReposts,
        totalShares,
        avgEngagementPercentage: avgEngagementPercentage?.toFixed(2),
        spentAmount: spentAmount?.toFixed(2),
        balanceRemaining: balanceRemaining?.toFixed(2),
      });
    };

    const updateLastPostTrackingTime = async () => {
      const lastedUpdateTimeList = selectedInfluencers?.map(
        (item) => item?.postTracking?.updatedAt
      );
      const sortedTimeList = lastedUpdateTimeList?.sort(
        (a, b) => new Date(b).getTime() - new Date(a).getTime()
      );
      setLastPostTrackingTime(sortedTimeList?.[0]);
    };

    updateLastPostTrackingTime();
    overallPerformance();
  }, [selectedInfluencers]);
  useEffect(() => {
    let totalAdditionalDiscounts = 0;
    collabData?.Budget?.additionalDiscounts?.forEach((item) => {
      totalAdditionalDiscounts = item?.amount;
    });
    const additionalAmounts =
      collaborationCharges * selectedInfluencers?.length +
      usedCampaignAddOn * campaignCharges -
      (usedCollaborationCredits * collaborationCharges + usedAnchorsCredits);

    const baseAmount = parseFloat(
      (
        collabData?.ROI?.safeExpense?.max -
        (discountedPrice || 0) +
        additionalAmounts
      )?.toString()
    );
    const GST = baseAmount * 0.18;
    setAmount(
      baseAmount + GST - collabStats?.paidAlready - totalAdditionalDiscounts
    );
    setGst(GST);
  }, [collabStats, collabData?.ROI?.safeExpense?.max]);
  useEffect(() => {
    setAddFundsAmountGst(parseFloat((addFundsAmount * 0.18)?.toFixed(2)));
  }, [addFundsAmount]);

  const handleSubmitApprovalForm = async () => {
    generalState?.setLoading(true);
    await brandState
      ?.updateAnchorsCollabDetails(collabId, {
        influencerId: currentInfluencerID,
        brandApproval:
          ApproveSection === "Suggestion" && SuggestChanges?.length > 0
            ? "suggested"
            : "accepted",
        suggestChanges: ApproveSection === "Suggestion" ? SuggestChanges : "",
      })
      .then(async () => {
        const result = await brandState?.getCollabData(collabId);
        if (result) {
          setCollabData(result?.collab);
          SetSelectedInfluencers(result?.selectedInfluencers);
          setCollabStats({
            acceptedInfluencerLength: result?.acceptedInfluencerLength,
            draftSubmittedInfluencerLength:
              result?.draftSubmittedInfluencerLength,
            approvedDraftLength: result?.approvedDraftLength,
            suggestedChangesLength: result?.suggestedChangesLength,
            currentStatus: result?.currentStatus,
            paidAlready: result?.paidAlready,
            isLiveDatePresent: result?.isLiveDatePresent,
          });
          setApproveSection("Approved");
          setSuggestChanges(null);
        }
        generalState?.setLoading(false);
        toast({
          title:
            ApproveSection === "Suggestion"
              ? "Suggestion accepted successfully"
              : "Acceptance accepted successfully",
        });
        setOpenApprovePopup(false);
      })
      .catch(() => {
        generalState?.setLoading(false);
        toast({
          title: "Failed to accept/suggest change",
          variant: "destructive",
        });
      });
  };

  const handleTrackPost = async (
    index: number,
    collabId: string,
    postUrl: string,
    influencerId: string
  ) => {
    try {
      if (collabId === "sample") {
        return toast({
          title: `Tracking refreshed for ${index + 1} influencer post`,
        });
      }

      const result = await brandState?.updatePostTracking(
        collabId,
        postUrl,
        influencerId
      );

      if (result?.success) {
        const result2 = await brandState?.getCollabData(collabId);
        SetSelectedInfluencers(result2?.selectedInfluencers);
        toast({
          title: `Tracking refreshed for ${index + 1} influencer post`,
        });
        // SetSelectedInfluencers((prevState) => {
        //   const updatedInfluencers = [...prevState];
        //   if (updatedInfluencers[index]) {
        //     console.log(updatedInfluencers[index]);
        //     updatedInfluencers[index] = {
        //       ...updatedInfluencers[index],
        //       postTracking: result?.postTracking,
        //     };
        //   }
        //   return updatedInfluencers;
        // });
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleUpdateAllPostTracking = async (collabId: string) => {
    setLoadingTracking(true);

    const influencerPostList = selectedInfluencers
      ?.filter((item) => item?.trackingLink !== undefined)
      ?.map((item, index) => ({
        index,
        anchorsCollabId: item?._id,
        postUrl: item?.trackingLink,
      }));

    await Promise.all(
      influencerPostList.map((item) =>
        handleTrackPost(
          item.index,
          collabId,
          item.postUrl,
          item?.anchorsCollabId
        )
      )
    );

    setLoadingTracking(false);
  };

  const handlePayment = async () => {
    generalState?.setLoading(true);

    await handlePaymentClick({
      paymentInfo: "Live Campaign",
      Amount,
      subject: "Live Campaign",
      onSuccess: async (result) => {
        await brandState
          ?.saveCollabData({
            collabId: collabId,
            step: 6,
            data: {
              Budget: {
                makeLiveBill: result?.order,
              },
            },
          })
          .then(async () => {
            const result = await brandState?.getCollabData(collabId);
            if (result) {
              setCollabData(result?.collab);
              SetSelectedInfluencers(result?.selectedInfluencers);
              setCollabStats({
                acceptedInfluencerLength: result?.acceptedInfluencerLength,
                draftSubmittedInfluencerLength:
                  result?.draftSubmittedInfluencerLength,
                approvedDraftLength: result?.approvedDraftLength,
                suggestedChangesLength: result?.suggestedChangesLength,
                currentStatus: result?.currentStatus,
                paidAlready: result?.paidAlready,
                isLiveDatePresent: result?.isLiveDatePresent,
              });
              setApproveSection("Approved");
              setSuggestChanges(null);
              setOpenSuccessPage(true);
            }
          })
          .catch(() => {
            generalState?.setLoading(false);
            toast({
              title: "Failed to save changes",
              variant: "destructive",
            });
          });
      },
      gst: gst,
      pg_charges: selectedPaymentMethod === "card" ? Amount * 0.023 : 0,
      collabId,
    });
  };

  const handleAddFunds = async () => {
    generalState?.setLoading(true);

    await handlePaymentClick({
      paymentInfo: "Recharge Funds",
      Amount: addFundsAmount + addFundsAmountGst,
      subject: "Recharge Funds",
      onSuccess: async (result) => {
        const addedFunds = collabData?.Budget?.addedFunds || [];
        addedFunds?.push(result?.order);
        await brandState?.saveCollabData({
          collabId: collabId,
          step: 6,
          data: {
            Budget: {
              addedFunds,
              addedFundsAmount:
                collabData?.Budget?.addedFundsAmount || 0 + addFundsAmount,
              couponId: collabData?.Budget?.couponId,
            },
          },
        });
        toast({
          title: "Funds recharged successfully",
          description: `₹${addFundsAmount} Added to your Funds`,
          className: "bg-green-500 text-white font-bold ",
        });
        setCurrentStep(4);
        window.location.reload();
      },
      gst: addFundsAmountGst,
    });
  };

  const handleSubmitSuggestion = async ({
    comment,
    editorState,
    images,
    isApprove,
  }: {
    comment: string;
    editorState: { influencerId: string; docid: string };
    images: File[];
    isApprove: boolean;
  }) => {
    if (!(comment?.trim()?.length > 0)) {
      toast({
        title: "Please add your suggestion",
        variant: "destructive",
      });
      return true;
    }
    try {
      const savedImages = await Promise.all(
        images?.map(async (item, index) => {
          if (item instanceof Blob) {
            const newFileName = `${index + 1}${item.name.slice(
              item.name.lastIndexOf(".")
            )}`; // Custom name with original extension

            const renamedFile = new File([item], newFileName, {
              type: item.type,
              lastModified: item.lastModified,
            });
            const formData = new FormData();
            formData.append("file", renamedFile);
            const result = await brandState?.uploadBrandCollabImage(formData);
            return result?.success ? result?.result?.Location : item;
          }
          return item;
        })
      );
      if (collabId === "sample") {
        const index = selectedInfluencers?.findIndex(
          (item) => item?.influencerDetails?._id === editorState?.influencerId
        );
        const postText = [
          `Ordered from Zepto Cafe for the 1st time today.
Some thoughts:
1. USP vis-a-vis Zom/Swiggy is delivery in 10 mins, took 28 mins for me. that too for a central place in Indiranagar

2. Pricing - spot on for small purchases. Same tea on Zomato with all the fees is 230+. ZC was just 140

3. Before ordering, taste was an apprehension for me. But ZC doesn't have their own recipe. It came in a Chai Point kettle. The interesting part here is that they don't use Chai Point's name on their app anywhere. Maybe so that they can eventually turn this into their private label or maybe to have flexibility to change vendors when they want to?

4. Selection - love it. They have everything from breakfast essentials to a full fledged meal. Stuff like Pizza, dal makhani & even chole bhature

5. Freshly prepared - this is what they claim. But they also claim to deliver it in under 10 mins. Kinda hard to believe. Would love to learn more about this 

6. Removes brand decision fatigue - once they get you to trust ZC's taste, you won't have to decide which brand's pizza you want to eat. In my mind, this becomes a big challenge in customers' mind while using Zom/Swiggy. Variety does fatigue you.

Whatever it is, seems like this is here to stay. If they can just deliver it quicker than Zom/Swiggy - there's a good large market who will hop onto it. Rooting for them - love their execution always :')`,
          `Gurgaon, Delhi, Mumbai and Bangalore can now order fresh food delivered in 10 minutes. 

Its crazy how much love we are getting for Zepto Cafe! From fresh brewed coffee to hot piping masala tea. From buttery Bun Maskas to steamy Baos. From crunchy Puffs to cheesy Garlic Bread, we are delivering within 10 minutes. And yes our Vietnamese Cold Coffee is everybody's favourite. 

Have you tried Zepto yet? Do tell me in the comments which one is your favourite. Soon to be in more cities!`,
        ];

        const postImage = [
          "https://anchors-collab.s3.ap-south-1.amazonaws.com/InfluBrandCollab/1741023605831-1.jpeg",
          "http://anchors-collab.s3.ap-south-1.amazonaws.com/InfluBrandCollab/1741023056974-1.jpeg",
        ];

        SetSelectedInfluencers((prev) => {
          const latestDraft = prev[index].draft[0];
          latestDraft.suggestedChanges = {
            comment,
            suggestedBy: authState?.loggedBrand?.BrandId,
            suggestedAt: new Date().toISOString(),
            status: 1,
          };
          prev[index].draft[0] = latestDraft;
          prev[index].draft.unshift({
            postText: postText?.[index],
            postImages: [postImage?.[index]],
            postVideos: [],
            status: 1,
            submittedAt: "2025-03-26T11:02:03.764Z",
            createdAt: "2025-03-26T11:01:17.593Z",
            updatedAt: "2025-03-26T11:01:17.593Z",
            _id: "67e3de7de2a7ddff421d665b",
          });
          prev[index].brandApproval = "suggested";
          const updatedInfluencers = [...prev];

          return updatedInfluencers;
        });

        setCollabStats((prev) => {
          return {
            ...prev,
            suggestedChangesLength: selectedInfluencers?.filter(
              (item) => item?.brandApproval === "suggested"
            )?.length,
          };
        });
        toast({
          title: "Suggestion added successfully",
        });
        return;
      }

      generalState?.setLoading(true);
      const currentInflu = await selectedInfluencers?.find((influ) => {
        if (influ?.influencerDetails?._id === editorState?.influencerId) {
          return influ;
        }
        return null;
      });
      if (!currentInflu) {
        return true;
      }

      try {
        await brandState?.updateAnchorsCollabDetails(collabId, {
          influencerId: editorState?.influencerId,
          brandApproval: "suggested",
          draft: {
            _id: editorState?.docid,
            suggestedChanges: {
              comment: comment,
              images: savedImages,
              suggestedBy: authState?.loggedBrand?.BrandId,
              suggestedAt: new Date().toISOString(),
            },
          },
        });

        if (isApprove) {
          await handleApproveDraft(editorState?.influencerId);
          generalState?.setLoading(false);
          return true; // Return the result of approve operation
        } else {
          const result = await brandState?.getCollabData(collabId);
          if (result) {
            setCollabData(result?.collab);
            SetSelectedInfluencers(result?.selectedInfluencers);
            setCollabStats({
              acceptedInfluencerLength: result?.acceptedInfluencerLength,
              draftSubmittedInfluencerLength:
                result?.draftSubmittedInfluencerLength,
              approvedDraftLength: result?.approvedDraftLength,
              suggestedChangesLength: result?.suggestedChangesLength,
              currentStatus: result?.currentStatus,
              paidAlready: result?.paidAlready,
              isLiveDatePresent: result?.isLiveDatePresent,
            });
          }
          generalState?.setLoading(false);

          if (collabData?.campaignInfo?.isSampleCampaign) {
            setChangesSuggested(true);
          }

          toast({
            title: "Suggestion added successfully",
          });
          return true;
        }
      } catch (error) {
        generalState?.setLoading(false);
        toast({
          title: "Failed to add suggestion",
          variant: "destructive",
        });
        return false;
      }
    } catch (error) {
      generalState?.setLoading(false);
      toast({
        title: "Error processing suggestion",
        variant: "destructive",
      });
      return false;
    }
  };

  const handleApproveDraft = async (influencerId: string) => {
    if (collabId === "sample") {
      const index = selectedInfluencers?.findIndex(
        (item) => item?.influencerDetails?._id === influencerId
      );
      SetSelectedInfluencers((prev) => {
        prev[index].brandApproval = "accepted";
        const updatedInfluencers = [...prev];

        return updatedInfluencers;
      });
      setCollabStats((prev) => {
        return {
          ...prev,
          // currentStatus: "Live",
          approvedDraftLength: selectedInfluencers?.filter(
            (item) => item?.brandApproval === "accepted"
          )?.length,
          suggestedChangesLength: selectedInfluencers?.filter(
            (item) => item?.brandApproval === "suggested"
          )?.length,
        };
      });
      toast({
        title: "Draft approved successfully",
      });
      return;
    }
    await brandState
      ?.updateAnchorsCollabDetails(collabId, {
        influencerId: influencerId,
        brandApproval: "accepted",
        approvedAt: new Date().toISOString(),
        approvedBy: authState?.loggedBrand?.BrandId,
      })
      .then(async () => {
        const result = await brandState?.getCollabData(collabId);
        if (result) {
          setCollabData(result?.collab);
          SetSelectedInfluencers(result?.selectedInfluencers);
          setCollabStats({
            acceptedInfluencerLength: result?.acceptedInfluencerLength,
            draftSubmittedInfluencerLength:
              result?.draftSubmittedInfluencerLength,
            approvedDraftLength: result?.approvedDraftLength,
            suggestedChangesLength: result?.suggestedChangesLength,
            currentStatus: result?.currentStatus,
            paidAlready: result?.paidAlready,
            isLiveDatePresent: result?.isLiveDatePresent,
          });
        }
        generalState?.setLoading(false);
        if (collabData?.campaignInfo?.isSampleCampaign) {
          setDraftApproved(true);
        }
        toast({
          title: "Draft approved successfully",
        });
      })
      .catch(() => {
        generalState?.setLoading(false);
        toast({
          title: "Failed to approve",
          variant: "destructive",
        });
      });
  };

  const handleSelectDate = async (
    influencerId: string,
    date: Date,
    name: string
  ) => {
    if (collabId === "sample") {
      const index = selectedInfluencers?.findIndex(
        (item) => item?.influencerDetails?._id === influencerId
      );
      const trackingLink = [
        "https://www.linkedin.com/feed/update/urn:li:activity:7239856507880644609/",
        "https://www.linkedin.com/feed/update/urn:li:activity:7264904534043369474/",
      ];
      SetSelectedInfluencers((prev) => {
        prev[index].liveAt = date;
        prev[index].trackingLink = trackingLink[index];
        const updatedInfluencers = [...prev];

        return updatedInfluencers;
      });

      setCollabStats((prev) => {
        console.log({ prev });
        return {
          ...prev,
          currentStatus: "Live",
          suggestedChangesLength: selectedInfluencers?.filter(
            (item) => item?.brandApproval === "suggested"
          )?.length,
        };
      });
      toast({
        title: `Live Date for ${name} set successfully`,
      });
      return;
    }

    await brandState
      ?.updateAnchorsCollabDetails(collabId, {
        influencerId: influencerId,
        liveAt: date,
      })
      .then(async () => {
        const result = await brandState?.getCollabData(collabId);
        if (result) {
          setCollabData(result?.collab);
          SetSelectedInfluencers(result?.selectedInfluencers);
          setCollabStats({
            acceptedInfluencerLength: result?.acceptedInfluencerLength,
            draftSubmittedInfluencerLength:
              result?.draftSubmittedInfluencerLength,
            approvedDraftLength: result?.approvedDraftLength,
            suggestedChangesLength: result?.suggestedChangesLength,
            currentStatus: result?.currentStatus,
            paidAlready: result?.paidAlready,
            isLiveDatePresent: result?.isLiveDatePresent,
          });
        }
        generalState?.setLoading(false);
        toast({
          title: `Live Date for ${name} set successfully `,
        });
      })
      .catch(() => {
        generalState?.setLoading(false);
        toast({
          title: "Failed to approve",
          variant: "destructive",
        });
      });
  };

  return {
    currentStep,
    collabData,
    selectedInfluencers,
    collabStats,
    masterCollabGoals,
    masterContentCategory,
    masterAudienceCategory,
    openApprovePopup,
    section,
    setCurrentStep,
    getCollabData,
    ApproveSection,
    draftApproved,
    changesSuggested,
    setDraftApproved,
    setChangesSuggested,
    setApproveSection,
    setOpenApprovePopup,
    SuggestChanges,
    setSuggestChanges,
    handleSubmitApprovalForm,
    collabId,
    sampleId,
    lastPostTrackingTime,
    overallPerformanceData,
    openSuccessPage,
    setOpenSuccessPage,
    addFundsAmount,
    setAddFundsAmount,
    addFundsAmountGst,
    handlePayment,
    handleAddFunds,
    handleSubmitSuggestion,
    handleApproveDraft,
    handleTrackPost,
    handleUpdateAllPostTracking,
    loadingTracking,
    handleSelectDate,

    //coupon
    coupon,
    discountedPrice,

    //payment
    selectedPaymentMethod,
    setSelectedPaymentMethod,
    collaborationCharges,
    campaignCharges,
    usedCollaborationCredits,
    usedCampaignAddOn,
    usedAnchorsCredits,
  };
};
